import React from 'react';
import { GitBranch, ClipboardCheck, Plug, Plus, Trash2 } from 'lucide-react';
import { Section, Toggle, Field, INPUT } from './callingAgentUi';

const SMALL = 'border border-[#D8DDE6] rounded-[4px] px-2 py-1.5 text-[12px] focus:outline-none focus:border-[#0176D3]';
const ADD = 'flex items-center gap-1 text-[12px] font-semibold text-[#0176D3] hover:underline';

// Edits one item of an array field without mutating the config in place.
const useList = (config, update, key, path = key) => {
  const list = path.split('.').reduce((o, k) => o?.[k], config) || [];
  return {
    list,
    set: (i, patch) => update(path, list.map((x, j) => (j === i ? { ...x, ...patch } : x))),
    add: (item) => update(path, [...list, item]),
    del: (i) => update(path, list.filter((_, j) => j !== i)),
  };
};

export default function CallFlowSections({ config, update }) {
  const flow = config?.flow || {};
  const steps = useList(config, update, 'steps', 'flow.steps');
  const dispositions = useList(config, update, 'dispositions');
  const fields = useList(config, update, 'extractFields');
  const tools = useList(config, update, 'customTools');
  const stepIds = steps.list.map((s) => s.id).filter(Boolean);

  const addStep = () => {
    const id = `step_${steps.list.length + 1}`;
    steps.add({ id, name: '', prompt: '', transitions: [] });
    if (!flow.startStepId) update('flow.startStepId', id);
  };
  const setTransitions = (i, transitions) => steps.set(i, { transitions });

  return (
    <>
      <Section title="Call Flow" icon={GitBranch}>
        <Toggle
          checked={!!flow.enabled}
          onChange={(v) => update('flow.enabled', v)}
          label="Guide the call through steps"
          description="Off = the agent follows the single instruction above. On = it follows one step at a time and moves on when a condition you describe is met."
        />
        {flow.enabled && (
          <>
            <Field label="Rules for every step">
              <textarea className={`${INPUT} resize-none`} rows={2} value={flow.globalPrompt || ''} onChange={(e) => update('flow.globalPrompt', e.target.value)} placeholder="e.g. Never promise a discount. Stay polite if the caller is rude." />
            </Field>
            {steps.list.map((s, i) => (
              <div key={i} className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-2">
                <div className="flex gap-2 items-center">
                  <input className={`${SMALL} w-32 font-mono`} value={s.id} onChange={(e) => steps.set(i, { id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="step_id" />
                  <input className={`${SMALL} flex-1`} value={s.name || ''} onChange={(e) => steps.set(i, { name: e.target.value })} placeholder="Step name (e.g. Greet)" />
                  <button type="button" onClick={() => steps.del(i)} aria-label="Remove step" className="text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
                </div>
                <textarea className={`${INPUT} resize-none`} rows={3} value={s.prompt || ''} onChange={(e) => steps.set(i, { prompt: e.target.value })} placeholder="What the agent should do in this step" />
                {(s.transitions || []).map((t, k) => (
                  <div key={k} className="flex gap-2 items-center">
                    <span className="text-[12px] text-[#706E6B]">then go to</span>
                    <select className={SMALL} value={t.to || ''} onChange={(e) => setTransitions(i, s.transitions.map((x, j) => (j === k ? { ...x, to: e.target.value } : x)))}>
                      <option value="">choose…</option>
                      {stepIds.filter((id) => id !== s.id).map((id) => <option key={id} value={id}>{id}</option>)}
                    </select>
                    <input className={`${SMALL} flex-1`} value={t.condition || ''} onChange={(e) => setTransitions(i, s.transitions.map((x, j) => (j === k ? { ...x, condition: e.target.value } : x)))} placeholder="when… (e.g. the caller gave their name)" />
                    <button type="button" onClick={() => setTransitions(i, s.transitions.filter((_, j) => j !== k))} aria-label="Remove transition" className="text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={13} /></button>
                  </div>
                ))}
                <button type="button" className={ADD} onClick={() => setTransitions(i, [...(s.transitions || []), { to: '', condition: '' }])}><Plus size={12} /> Add next step</button>
              </div>
            ))}
            <button type="button" className={ADD} onClick={addStep}><Plus size={13} /> Add step</button>
            {stepIds.length > 0 && (
              <Field label="First step">
                <select className={INPUT} value={flow.startStepId || ''} onChange={(e) => update('flow.startStepId', e.target.value)}>
                  {stepIds.map((id) => <option key={id} value={id}>{id}</option>)}
                </select>
              </Field>
            )}
            <p className="text-[11px] text-[#706E6B]">A step with no next step is the last one: the agent ends the call when it is done.</p>
          </>
        )}
      </Section>

      <Section title="After the Call" icon={ClipboardCheck}>
        <Toggle
          checked={config?.voicemail?.detect !== false}
          onChange={(v) => update('voicemail.detect', v)}
          label="Hang up on answering machines"
          description="On outbound calls the agent ends the call without leaving a message, and the contact can be retried in a campaign."
        />
        <Field label="Outcome labels the AI may choose from">
          {dispositions.list.map((d, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input className={`${SMALL} w-36 font-mono`} value={d.key || ''} onChange={(e) => dispositions.set(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="interested" />
              <input className={`${SMALL} flex-1`} value={d.description || ''} onChange={(e) => dispositions.set(i, { description: e.target.value })} placeholder="When to use it (e.g. wants a demo)" />
              <button type="button" onClick={() => dispositions.del(i)} aria-label="Remove outcome" className="text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" className={ADD} onClick={() => dispositions.add({ key: '', description: '' })}><Plus size={13} /> Add outcome</button>
        </Field>
        <Field label="Details to collect from the conversation">
          {fields.list.map((f, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <input className={`${SMALL} w-36 font-mono`} value={f.name || ''} onChange={(e) => fields.set(i, { name: e.target.value.replace(/[^A-Za-z0-9_]/g, '_') })} placeholder="budget" />
              <input className={`${SMALL} flex-1`} value={f.description || ''} onChange={(e) => fields.set(i, { description: e.target.value })} placeholder="What it is (e.g. monthly budget in rupees)" />
              <button type="button" onClick={() => fields.del(i)} aria-label="Remove field" className="text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
            </div>
          ))}
          <button type="button" className={ADD} onClick={() => fields.add({ name: '', description: '' })}><Plus size={13} /> Add detail</button>
        </Field>
        <Field label="Score every call (optional)">
          <textarea className={`${INPUT} resize-none`} rows={2} value={config?.qaCriteria || ''} onChange={(e) => update('qaCriteria', e.target.value)} placeholder="Describe a good call, e.g. greets by name, confirms the demo date, never interrupts. Each call gets a 1-10 score." />
        </Field>
      </Section>

      <Section title="Tools the Agent Can Use" icon={Plug}>
        <p className="text-[12px] text-[#706E6B]">Let the agent look something up in your own system during a call. Use {'{{name}}'} in the URL for a value the agent fills in, and list each value below.</p>
        {tools.list.map((t, i) => (
          <div key={i} className="border border-[#D8DDE6] rounded-[4px] p-3 space-y-2">
            <div className="flex gap-2">
              <input className={`${SMALL} w-40 font-mono`} value={t.name || ''} onChange={(e) => tools.set(i, { name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} placeholder="order_status" />
              <select className={SMALL} value={t.method || 'GET'} onChange={(e) => tools.set(i, { method: e.target.value })}><option>GET</option><option>POST</option></select>
              <input className={`${SMALL} flex-1`} value={t.url || ''} onChange={(e) => tools.set(i, { url: e.target.value })} placeholder="https://api.yourshop.com/orders/{{order_id}}" />
              <button type="button" onClick={() => tools.del(i)} aria-label="Remove tool" className="text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
            </div>
            <input className={`${SMALL} w-full`} value={t.description || ''} onChange={(e) => tools.set(i, { description: e.target.value })} placeholder="When the agent should use it (e.g. Look up the status of an order)" />
            <input className={`${SMALL} w-full`} value={(t.params || []).map((p) => p.name).join(', ')} onChange={(e) => tools.set(i, { params: e.target.value.split(',').map((x) => x.trim().replace(/[^A-Za-z0-9_]/g, '')).filter(Boolean).map((name) => ({ name })) })} placeholder="Values the agent fills in, comma separated (e.g. order_id)" />
            <div className="flex gap-2">
              <input className={`${SMALL} w-40`} value={Object.keys(t.headers || {})[0] || ''} onChange={(e) => tools.set(i, { headers: e.target.value ? { [e.target.value]: Object.values(t.headers || {})[0] || '' } : {} })} placeholder="Auth header (optional)" />
              <input className={`${SMALL} flex-1`} type="password" autoComplete="off" value={Object.values(t.headers || {})[0] || ''} onChange={(e) => tools.set(i, { headers: { [Object.keys(t.headers || {})[0] || 'Authorization']: e.target.value } })} placeholder="Header value, e.g. Bearer sk_live_…" />
            </div>
          </div>
        ))}
        <button type="button" className={ADD} onClick={() => tools.add({ name: '', description: '', method: 'GET', url: '', params: [], headers: {} })}><Plus size={13} /> Add tool</button>
      </Section>
    </>
  );
}
